import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../api/client';
import { SkeletonBar, Button } from '../components/ui';
import { T } from '../styles/tokens';

// ─── State ────────────────────────────────────────────────────────────

const referrals = signal<any[]>([]);
const stats = signal<any>(null);
const config = signal<any>(null);
const loading = signal(true);
const activeTab = signal<'overview' | 'referrals' | 'fraud' | 'config'>('overview');
const filterStatus = signal<string>('');
const total = signal(0);

async function fetchData() {
  loading.value = true;
  try {
    const [refData, statsData, configData] = await Promise.all([
      api.get<{ referrals: any[]; total: number }>(`/admin/cloud/referrals?limit=50${filterStatus.value ? `&status=${filterStatus.value}` : ''}`),
      api.get<any>('/admin/cloud/referral-stats'),
      api.get<any>('/admin/cloud/referral-config'),
    ]);
    referrals.value = refData.referrals;
    total.value = refData.total;
    stats.value = statsData;
    config.value = configData;
  } catch (err) {
    console.error('Failed to fetch admin referral data', err);
  }
  loading.value = false;
}

async function processEvents() {
  try {
    const res = await api.post<{ processed: number }>('/admin/cloud/referrals/process-events', {});
    alert(`Processados: ${res.processed} eventos`);
    await fetchData();
  } catch (err) {
    console.error(err);
  }
}

// ─── Styles ───────────────────────────────────────────────────────────

const S = {
  page: { padding: '24px 28px', maxWidth: 1200, margin: '0 auto' } as const,
  title: { fontSize: 22, fontWeight: 700, color: T.text, margin: 0 } as const,
  subtitle: { fontSize: 13, color: T.textSecondary, marginTop: 4 } as const,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, margin: '24px 0' } as const,
  card: { background: T.bgSecondary, borderRadius: 12, padding: '20px', border: `1px solid ${T.border}` } as const,
  cardLabel: { fontSize: 12, color: T.textSecondary, marginBottom: 6 } as const,
  cardValue: { fontSize: 24, fontWeight: 700, color: T.text } as const,
  cardSub: { fontSize: 11, color: T.textMuted, marginTop: 4 } as const,
  tabs: { display: 'flex', gap: 8, marginBottom: 24 } as const,
  tab: (active: boolean) => ({
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: active ? '#6c5ce7' : '#1a1d2e', color: active ? '#fff' : T.textSecondary,
  }) as const,
  table: { width: '100%', borderCollapse: 'collapse' as const } as const,
  th: { textAlign: 'left' as const, padding: '10px 12px', fontSize: 11, color: T.textMuted, borderBottom: `1px solid ${T.border}`, fontWeight: 600 } as const,
  td: { padding: '10px 12px', fontSize: 13, color: T.textSecondary, borderBottom: `1px solid ${T.bgCard}` } as const,
  badge: (color: string) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    background: `${color}20`, color,
  }) as const,
  btn: (color: string) => ({
    padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: color, color: T.text,
  }) as const,
  select: { padding: '6px 12px', borderRadius: 6, fontSize: 12, background: T.bgSecondary, color: T.textSecondary, border: `1px solid ${T.borderHover}` } as const,
  configRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` } as const,
  configLabel: { fontSize: 13, color: T.textSecondary } as const,
  configValue: { fontSize: 13, fontWeight: 600, color: '#6c5ce7' } as const,
};

const STATUS_COLORS: Record<string, string> = {
  pending: T.warning, signup: '#3498db', activated: T.success, paid: '#27ae60', blocked: T.danger, churned: T.textMuted,
};

// ─── Component ────────────────────────────────────────────────────────

export function AdminReferralsPage() {
  useEffect(() => { fetchData(); }, []);

  if (loading.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);

  const s = stats.value;

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
 <h1 style={S.title}>Indique & Gain — Admin</h1>
          <p style={S.subtitle}>Gerenciar referrals, fraude e configurações</p>
        </div>
 <Button variant="primary" onClick={processEvents} style={{ background: T.accent }}>Processar Eventos</Button>
      </div>

      {/* KPIs */}
      <div style={S.grid}>
        <div style={S.card}>
          <div style={S.cardLabel}>Total Indicações</div>
          <div style={S.cardValue}>{s?.total_referrals || 0}</div>
        </div>
        <div style={S.card}>
          <div style={S.cardLabel}>Taxa de Ativação</div>
          <div style={{ ...S.cardValue, color: T.success }}>{s?.activation_rate || 0}%</div>
        </div>
        <div style={S.card}>
          <div style={S.cardLabel}>Total USD Gasto</div>
          <div style={{ ...S.cardValue, color: T.warning }}>${(s?.total_rewards_usd || 0).toFixed(2)}</div>
        </div>
        <div style={S.card}>
          <div style={S.cardLabel}>Por Status</div>
          <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 8 }}>
            {Object.entries(s?.by_status || {}).map(([status, count]) => (
              <div key={status} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={S.badge(STATUS_COLORS[status] || T.textSecondary)}>{status}</span>
                <span style={{ fontWeight: 600, color: T.text }}>{count as number}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {(['overview', 'referrals', 'fraud', 'config'] as const).map(tab => (
          <Button variant="ghost" key={tab} style={S.tab(activeTab.value === tab)} onClick={() => { activeTab.value = tab; }}>
 {tab === 'overview' ? 'Overview' : tab === 'referrals' ? 'Referrals' : tab === 'fraud' ? 'Fraude' : 'Config'}
          </Button>
        ))}
      </div>

      {/* Content */}
      {activeTab.value === 'overview' && <TopReferrersSection topReferrers={s?.top_referrers || []} recentActivity={s?.recent_activity || []} />}
      {activeTab.value === 'referrals' && <ReferralsListSection />}
      {activeTab.value === 'fraud' && <FraudSection />}
      {activeTab.value === 'config' && <ConfigSection />}
    </div>
  );
}

function TopReferrersSection({ topReferrers, recentActivity }: { topReferrers: any[]; recentActivity: any[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div style={S.card}>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 }}>Top Parceiros</div>
        {topReferrers.map((r: any, i: number) => (
          <div key={r.referrer_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.bgCard}` }}>
            <span style={{ fontSize: 13, color: T.textSecondary }}>#{i + 1} {r.referrer_id.substring(0, 8)}...</span>
            <span style={{ fontSize: 13, color: T.textSecondary }}>{r.referral_count} refs</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.success }}>${(r.total_earned || 0).toFixed(2)}</span>
          </div>
        ))}
        {!topReferrers.length && <div style={{ color: T.textMuted, fontSize: 13 }}>Nenhum parceiro ainda</div>}
      </div>
      <div style={S.card}>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 }}>Atividade Recente</div>
        {recentActivity.map((r: any) => (
          <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.bgCard}` }}>
            <span style={S.badge(STATUS_COLORS[r.status] || T.textSecondary)}>{r.status}</span>
            <span style={{ fontSize: 12, color: T.textSecondary }}>{new Date(r.created_at).toLocaleDateString('pt-BR')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReferralsListSection() {
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Todas as Indicações ({total.value})</div>
        <select style={S.select} value={filterStatus.value} onChange={(e) => { filterStatus.value = (e.target as HTMLSelectElement).value; fetchData(); }}>
          <option value="">Todos</option>
          <option value="pending">Pendente</option>
          <option value="signup">Cadastro</option>
          <option value="activated">Ativado</option>
          <option value="paid">Pagante</option>
          <option value="blocked">Bloqueado</option>
        </select>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Referrer</th>
            <th style={S.th}>Referree</th>
            <th style={S.th}>Status</th>
            <th style={S.th}>Fonte</th>
            <th style={S.th}>Risco</th>
            <th style={S.th}>Recompensa</th>
            <th style={S.th}>Data</th>
          </tr>
        </thead>
        <tbody>
          {referrals.value.map(r => (
            <tr key={r.id}>
              <td style={S.td}>{r.referrer_id.substring(0, 8)}...</td>
              <td style={S.td}>{r.referree_id ? `${r.referree_id.substring(0, 8)}...` : '—'}</td>
              <td style={S.td}><span style={S.badge(STATUS_COLORS[r.status] || T.textSecondary)}>{r.status}</span></td>
              <td style={S.td}>{r.source}</td>
              <td style={S.td}>
                <span style={S.badge(r.risk_level === 'high' ? T.danger : r.risk_level === 'medium' ? T.warning : T.success)}>
                  {r.risk_level} ({r.fraud_score})
                </span>
              </td>
              <td style={{ ...S.td, fontWeight: 600, color: T.success }}>${(r.total_paid || 0).toFixed(2)}</td>
              <td style={S.td}>{new Date(r.created_at).toLocaleDateString('pt-BR')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FraudSection() {
  const suspicious = referrals.value.filter(r => r.is_suspicious || r.fraud_score > 30);
  return (
    <div style={S.card}>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 }}>Análise de Fraude</div>
      <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16 }}>
 {suspicious.length} conta(s) sinalizadas
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Referrer</th>
            <th style={S.th}>IP</th>
            <th style={S.th}>Device</th>
            <th style={S.th}>Risco</th>
            <th style={S.th}>Score</th>
          </tr>
        </thead>
        <tbody>
          {suspicious.map(r => (
            <tr key={r.id}>
              <td style={S.td}>{r.referrer_id.substring(0, 8)}...</td>
              <td style={S.td}>{r.ip_address || '—'}</td>
              <td style={S.td}>{r.device_fingerprint ? `${r.device_fingerprint.substring(0, 12)}...` : '—'}</td>
              <td style={S.td}><span style={S.badge(r.risk_level === 'high' ? T.danger : T.warning)}>{r.risk_level}</span></td>
              <td style={S.td}>{r.fraud_score}</td>
            </tr>
          ))}
          {!suspicious.length && (
 <tr><td style={{ ...S.td, textAlign: 'center', color: T.textMuted }} colSpan={5}>Nenhuma conta suspeita </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ConfigSection() {
  const cfg = config.value;
  if (!cfg) return null;

  const configItems = [
    { key: 'base_reward', label: 'Recompensa Base (USD)', value: `$${cfg.base_reward}` },
    { key: 'tokens_per_usd', label: 'Tokens por USD', value: cfg.tokens_per_usd },
    { key: 'monthly_bonus', label: 'Bônus Mensal por Ativo', value: `$${cfg.monthly_bonus}` },
    { key: 'max_yearly_per_referral', label: 'Máx/Ano por Indicado', value: `$${cfg.max_yearly_per_referral}` },
    { key: 'credit_delay_hours', label: 'Delay de Crédito', value: `${cfg.credit_delay_hours}h` },
    { key: 'max_referrals_per_ip_day', label: 'Máx Referrals/IP/Dia', value: cfg.max_referrals_per_ip_day },
    { key: 'max_referrals_per_user_month', label: 'Máx Referrals/Usuário/Mês', value: cfg.max_referrals_per_user_month },
  ];

  return (
    <div style={S.card}>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 }}>Configurações</div>
      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 16 }}>
        Estágios de recompensa: Cadastro {(cfg.activation_stages?.signup * 100)}% • Ativação {(cfg.activation_stages?.activation * 100)}% • Pagante {(cfg.activation_stages?.paid * 100)}%
      </div>
      {configItems.map(item => (
        <div key={item.key} style={S.configRow}>
          <span style={S.configLabel}>{item.label}</span>
          <span style={S.configValue}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}
